import {ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Inject, OnDestroy, OnInit, Output} from "@angular/core";
import {OverlayRef} from "@angular/cdk/overlay";
import {Actions, ofType} from "@ngrx/effects";
import {PANEL_DATA} from "../../../../commons/angular/services/panel.service";
import {VisitPlanHttpService} from "../../services/visit-plan-http.service";
import {finalize, map, Observable, Subscription, take} from "rxjs";
import {AuditLogParserService, AuditLogStatus, VisitPlanLog} from "../../../../commons/angular/services/audit-log-parser.service";
import {VISIT_PLAN_AUDIT_LOG_FILTERS} from "../../constants/scheduling-project.constant";
import {BringozDateRange} from "../../../../commons/angular/models/bringoz-date-range.model";
import {AgentsFacadeService} from "../../../../agents/angular/state/agents-facade.service";
import {AgentsAPiActions} from "../../../../agents/angular/state/agents.actions";
import {AgentModel} from "../../../../agents/angular/models/agent.model";
import {AccountsFacadeService} from "../../../../account/angular/services/accounts-facade.service";
import {AccountsAPiActions} from "../../../../account/angular/state/accounts.actions";
import {AccountModel} from "../../../../account/angular/models/account.model";
import {UserNamePipe} from "../../../../commons/angular/pipes/user-name.pipe";
import {flatMap, uniq} from "lodash";
import {
  DEFAULT_VISIT_PLAN_AUDIT_LOG_COLUMN_WIDTHS,
  VISIT_PLAN_AUDIT_LOG_ROW_HEIGHT,
  VISIT_PLAN_AUDIT_LOG_TABLE_UNIQUE_ID
} from "../../../constants/visit-plan-audit-log-column-widths.constant";

interface VisitPlanLogWithDisplay extends VisitPlanLog {
  agentDisplay?: string[];
  accountDisplay?: string[];
}

require('./visit-plan-audit-log.scss');
@Component({
  standalone: false,
  selector: 'app-visit-plan-audit-log',
  templateUrl: './visit-plan-audit-log.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class VisitPlanAuditLogComponent implements OnInit, OnDestroy {

  public readonly AuditLogStatus = AuditLogStatus;

  @Output() onClose = new EventEmitter<void>();

  public readonly tableUniqueId: string = VISIT_PLAN_AUDIT_LOG_TABLE_UNIQUE_ID;
  public readonly defaultWidths: { [key: string]: number } = DEFAULT_VISIT_PLAN_AUDIT_LOG_COLUMN_WIDTHS;
  public readonly rowHeight: number = VISIT_PLAN_AUDIT_LOG_ROW_HEIGHT;
  public readonly skeletonRows: undefined[] = Array.from({length: 10});

  public visitPlan: any;

  public selectedOperation: string = 'ALL';
  public fromDate: number;
  public toDate: number;
  public selectedRange: BringozDateRange;
  public customTemplates: BringozDateRange[];

  public size: number = 20;

  public isLoading: boolean = false;
  public isInitialized: boolean = false;
  public auditLogs: VisitPlanLogWithDisplay[] = [];
  public nextCursor?: string;

  private sub: Subscription;
  private agentsSub?: Subscription;
  private accountsSub?: Subscription;

  // Filter Operations list
  public filterOperations = VISIT_PLAN_AUDIT_LOG_FILTERS;

  constructor(
    @Inject(PANEL_DATA) public data: { visitPlan: any },
    @Inject(OverlayRef) private overlayRef: OverlayRef,
    @Inject(ChangeDetectorRef) private cdr: ChangeDetectorRef,
    @Inject(VisitPlanHttpService) private visitPlanHttpService: VisitPlanHttpService,
    @Inject(AuditLogParserService) private auditLogParserService: AuditLogParserService,
    @Inject(AgentsFacadeService) private agentsFacadeService: AgentsFacadeService,
    @Inject(AccountsFacadeService) private accountsFacadeService: AccountsFacadeService,
    @Inject(Actions) private actions$: Actions,
    @Inject(UserNamePipe) private userNamePipe: UserNamePipe,
    @Inject('dateRangeTimeFiltersService') private dateRangeTimeFiltersService: any
  ) {
    this.visitPlan = this.data?.visitPlan;
  }

  ngOnInit(): void {
    document.body.classList.add('audit-log-active');
    this.customTemplates = this.dateRangeTimeFiltersService.getDeliveriesCustomTemplates();
    this.selectedRange = this.dateRangeTimeFiltersService.thisMonth();
    this.fromDate = this.selectedRange.dateStart;
    this.toDate = this.selectedRange.dateEnd;
    this.fetchLogs();
  }

  ngOnDestroy(): void {
    document.body.classList.remove('audit-log-active');
    this.sub?.unsubscribe();
    this.agentsSub?.unsubscribe();
    this.accountsSub?.unsubscribe();
  }

  public fetchLogs(isLoadMore: boolean = false): void {
    if (!this.visitPlan || !this.visitPlan.id) {
      return;
    }

    // Guard against redundant re-fetches when logs exist. Refreshing must go through
    // resetAndFetch() to ensure virtual scroll state and cursors are properly cleared.
    if (this.isLoading || (!isLoadMore && this.auditLogs.length > 0)) {
      return;
    }

    this.isLoading = true;
    this.cdr.detectChanges();

    const params: any = {
      limit: this.size
    };

    if (isLoadMore && this.nextCursor) {
      params.cursor = this.nextCursor;
    }

    // Map selected operation to server operations filter
    if (this.selectedOperation && this.selectedOperation !== 'ALL') {
      params.operations = this.selectedOperation;
    }

    // Date From Filter
    if (this.fromDate) {
      params.from = this.fromDate;
    }

    // Date To Filter
    if (this.toDate) {
      params.to = this.toDate;
    }

    this.sub?.unsubscribe();
    this.sub = this.visitPlanHttpService.getVisitPlanAuditLogs(this.visitPlan.id, params)
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.isInitialized = true;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: (res) => {
          const newList = res.list || [];
          const parsedLogs: VisitPlanLogWithDisplay[] = this.auditLogParserService.getVisitPlanLogs(newList);
          if (isLoadMore) {
            this.auditLogs = [...this.auditLogs, ...parsedLogs];
          } else {
            this.auditLogs = parsedLogs;
          }
          this.nextCursor = res.cursor;
          this.resolveEntityNames(parsedLogs);
          this.cdr.detectChanges();
        },
        error: (err) => {
          console.error('Error fetching audit logs:', err);
          if (!isLoadMore) {
            this.auditLogs = [];
          }
        }
      });
  }

  onFetchNext(): void {
    if (!this.nextCursor || this.isLoading) {
      return;
    }
    this.fetchLogs(true);
  }

  resetAndFetch(): void {
    this.sub?.unsubscribe();
    this.isLoading = false;
    this.auditLogs = [];
    this.nextCursor = undefined;
    this.fetchLogs();
  }

  onOperationChange(operation: string): void {
    this.selectedOperation = operation;
    this.resetAndFetch();
  }

  onRangeChange(range: any): void {
    if (range) {
      this.selectedRange = range;
      this.fromDate = range.dateStart;
      this.toDate = range.dateEnd;
      this.resetAndFetch();
    }
  }

  onCancel(): void {
    this.onClose.emit();
  }

  private resolveEntityNames(logs: VisitPlanLogWithDisplay[]): void {
    this.resolveAgents(logs);
    this.resolveAccounts(logs);
  }

  private resolveAgents(logs: VisitPlanLogWithDisplay[]): void {
    const ids = uniq(flatMap(logs, log => log.agentIds));
    if (!ids.length) return;

    this.agentsFacadeService.getMany({ ids });
    this.agentsSub?.unsubscribe();
    this.agentsSub = this.actions$.pipe(
      ofType(AgentsAPiActions.getAgentsSuccess, AgentsAPiActions.getAgentsFailure),
      take(1)
    ).subscribe(action => {
      if (action.type === AgentsAPiActions.getAgentsSuccess.type) {
        const nameMap = new Map(action.agents.map(a => [a.id, this.userNamePipe.transform(a.user || a)]));
        logs.forEach(log => {
          log.agentDisplay = log.agentIds.map(id => nameMap.get(id)).filter((name): name is string => !!name);
        });
        this.cdr.detectChanges();
      }
    });
  }

  private resolveAccounts(logs: VisitPlanLogWithDisplay[]): void {
    const ids = uniq(flatMap(logs, log => log.accountIds));
    if (!ids.length) return;

    this.accountsFacadeService.getMany({ ids });
    this.accountsSub?.unsubscribe();
    this.accountsSub = this.actions$.pipe(
      ofType(AccountsAPiActions.getAccountsSuccess, AccountsAPiActions.getAccountsFailure),
      take(1)
    ).subscribe(action => {
      if (action.type === AccountsAPiActions.getAccountsSuccess.type) {
        const nameMap = new Map(action.accounts.map(a => [a.id, a.name]));
        logs.forEach(log => {
          log.accountDisplay = log.accountIds.map(id => nameMap.get(id)).filter((name): name is string => !!name);
        });
        this.cdr.detectChanges();
      }
    });
  }
}
